import { addDays, format, startOfWeek } from "date-fns";
import {
  addDaysToCivilDate,
  differenceInCivilDays,
  parseDateAsUtcMidnight,
  range,
} from "utils";

export interface CalendarDateRange {
  startDate: string;
  endDate: string;
}

export interface CalendarWeekRange {
  startIndex: number;
  endIndex: number;
}

const CALENDAR_FIRST_DAY = "2019-12-30";
export const DAYS_PER_WEEK = 7;
const FOCUS_WEEK_OFFSET = 1;
const FUTURE_WEEK_COUNT = 10 * 52;
const LOAD_CHUNK_WEEK_COUNT = 4;
const MIN_PREFETCH_WEEK_COUNT = 4;

const TODAY_WEEK_INDEX = getCalendarWeekIndex(
  new Date().toISOString().slice(0, 10),
);

export const CALENDAR_WEEK_COUNT = TODAY_WEEK_INDEX + FUTURE_WEEK_COUNT;
export const CALENDAR_INITIAL_VISIBLE_WEEK_COUNT = 7;

export function getCalendarCellDate(cellId: number): string {
  return addDaysToCivilDate(CALENDAR_FIRST_DAY, cellId);
}

export function getCalendarCellId(date: string): number {
  return differenceInCivilDays(CALENDAR_FIRST_DAY, date);
}

export function getCalendarWeekIndex(date: string): number {
  return Math.floor(getCalendarCellId(date) / DAYS_PER_WEEK);
}

export function getCalendarWeekStart(weekIndex: number): string {
  return getCalendarCellDate(weekIndex * DAYS_PER_WEEK);
}

export function getCalendarWeekMonth(weekIndex: number): string {
  return getCalendarCellDate(weekIndex * DAYS_PER_WEEK + 3).slice(0, 7);
}

export function getCalendarFocusMonth(topWeekIndex: number): string {
  return getCalendarWeekMonth(topWeekIndex + FOCUS_WEEK_OFFSET);
}

export function getCalendarCellRangeWeekIndexes(
  startCellId: number,
  endCellId: number,
): number[] {
  return range(
    Math.floor(startCellId / DAYS_PER_WEEK),
    Math.floor(endCellId / DAYS_PER_WEEK) + 1,
  );
}

export function getCalendarInitialWeekIndex(date: string): number {
  return Math.min(
    Math.max(getCalendarWeekIndex(date), 0),
    CALENDAR_WEEK_COUNT - 1,
  );
}

export function getCalendarInitialWeeks(
  weekIndex: number,
  weekCount: number,
): CalendarWeekRange {
  return {
    startIndex: weekIndex,
    endIndex: Math.min(weekIndex + weekCount - 1, CALENDAR_WEEK_COUNT - 1),
  };
}

export function getCalendarWeeksToLoad(
  visibleWeeks: CalendarWeekRange,
): CalendarWeekRange {
  const prefetchWeekCount = Math.max(
    visibleWeeks.endIndex - visibleWeeks.startIndex + 1,
    MIN_PREFETCH_WEEK_COUNT,
  );

  return {
    startIndex: Math.max(visibleWeeks.startIndex - prefetchWeekCount, 0),
    endIndex: Math.min(
      visibleWeeks.endIndex + prefetchWeekCount,
      CALENDAR_WEEK_COUNT - 1,
    ),
  };
}

export function getCalendarWeekChunk(weekIndex: number): CalendarWeekRange {
  const startIndex = weekIndex - (weekIndex % LOAD_CHUNK_WEEK_COUNT);

  return {
    startIndex,
    endIndex: Math.min(
      startIndex + LOAD_CHUNK_WEEK_COUNT - 1,
      CALENDAR_WEEK_COUNT - 1,
    ),
  };
}

export function getCalendarWeekRangeDates(
  weeks: CalendarWeekRange,
): CalendarDateRange {
  return {
    startDate: getCalendarWeekStart(weeks.startIndex),
    endDate: getCalendarWeekStart(weeks.endIndex + 1),
  };
}

export function isCalendarDateInRange(
  date: string,
  range: CalendarDateRange,
): boolean {
  const civilDate = date.slice(0, 10);
  return civilDate >= range.startDate && civilDate < range.endDate;
}

export function getCalendarCellLabel(cellId: number, locale: string): string {
  const cellDate = getCalendarCellDate(cellId);
  const [, , day] = cellDate.split("-").map(Number);

  if (day === 1) {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(parseDateAsUtcMidnight(cellDate));
  }

  return String(day);
}

export function isCalendarCellToday(cellId: number): boolean {
  return getCalendarCellDate(cellId) === format(new Date(), "yyyy-MM-dd");
}

export function getCalendarWeekdayLabels(
  locale: string,
): { primary: string; secondary?: string }[] {
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const shortFormatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const narrowFormatter = new Intl.DateTimeFormat(locale, {
    weekday: "narrow",
  });

  return Array.from({ length: DAYS_PER_WEEK }, (_, i) => {
    const date = addDays(weekStart, i);
    return {
      primary: shortFormatter.format(date),
      secondary: narrowFormatter.format(date),
    };
  });
}
